import React from 'react';
import { Table, Tag, Button, Empty, Space } from 'antd';
import { ReloadOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { useQuery } from '@apollo/client';
import { GET_REVENUE_INVOICE_BY_BILL_ID, GET_INVOICE_SEND_RECORDS } from '../../graphql/queries/settlement/revenueInvoiceQueries';
import type { InvoiceSendRecordType, RevenueInvoiceType } from '../../graphql/types/settlement/revenueInvoice';
import { openBillExportFile } from '../../utils/billExportDownload';

interface InvoiceSendRecordsTabProps {
  billId: string;
}

/**
 * 账单详情页「Invoice 发送记录」Tab。
 * 先按 billId 拿当前生效 invoiceId，再分页查询该 Invoice 的推送记录。
 */
const InvoiceSendRecordsTab: React.FC<InvoiceSendRecordsTabProps> = ({ billId }) => {
  const { data: invoiceData, loading: invoiceLoading } = useQuery(GET_REVENUE_INVOICE_BY_BILL_ID, {
    variables: { billId },
    fetchPolicy: 'cache-and-network',
    skip: !billId,
  });

  const invoice: RevenueInvoiceType | null = invoiceData?.getRevenueInvoiceByBillId ?? null;
  const invoiceId = invoice?.invoiceId;

  const { data, loading, refetch } = useQuery(GET_INVOICE_SEND_RECORDS, {
    variables: { billId },
    fetchPolicy: 'cache-and-network',
    skip: !billId,
  });

  const records: InvoiceSendRecordType[] = data?.getInvoiceSendRecords?.content ?? [];

  const columns: ColumnsType<InvoiceSendRecordType> = [
    {
      title: '发送时间',
      dataIndex: 'sentAt',
      key: 'sentAt',
      width: 150,
      render: (v?: string | null) => (v ? new Date(v).toLocaleString('zh-CN') : '-'),
    },
    { title: 'Invoice 号', dataIndex: 'invoiceNo', key: 'invoiceNo', ellipsis: true },
    { title: '收件邮箱', dataIndex: 'recipientEmail', key: 'recipientEmail', ellipsis: true },
    {
      title: '发送文件',
      dataIndex: 'attachmentDownloadUrl',
      key: 'attachmentDownloadUrl',
      width: 100,
      render: (url?: string | null) =>
        url ? (
          <Button type="link" size="small" onClick={() => openBillExportFile(url)}>
            下载
          </Button>
        ) : (
          '-'
        ),
    },
    {
      title: '状态',
      dataIndex: 'sendStatus',
      key: 'sendStatus',
      width: 100,
      render: (status?: string | null) =>
        status === 'SUCCESS' ? <Tag color="green">成功</Tag> : <Tag color="red">失败</Tag>,
    },
    { title: '发送人', dataIndex: 'operatorName', key: 'operatorName', width: 120, ellipsis: true },
    {
      title: '失败原因',
      dataIndex: 'errorMessage',
      key: 'errorMessage',
      ellipsis: true,
      render: (msg?: string | null) => msg || '-',
    },
  ];

  if (!invoiceLoading && !invoiceId) {
    return <Empty description="该账单暂未开具 Invoice" />;
  }

  return (
    <div className="max-w-full overflow-hidden">
      <div className="mb-4 flex justify-end">
        <Space>
          <Button icon={<ReloadOutlined />} onClick={() => refetch()} loading={loading} disabled={!invoiceId}>
            刷新
          </Button>
        </Space>
      </div>
      <Table
        columns={columns}
        dataSource={records}
        rowKey="id"
        loading={invoiceLoading || loading}
        size="small"
        pagination={{ defaultPageSize: 10, showSizeChanger: true, pageSizeOptions: ['10', '20', '50'] }}
        scroll={{ x: 1000 }}
        tableLayout="fixed"
      />
    </div>
  );
};

export default InvoiceSendRecordsTab;
